import React, { useState, useEffect } from 'react';
import {
  Search, Filter, Download, Upload, MoreHorizontal,
  ExternalLink, UserCheck, Trash2, Mail, Phone,
  FileText, ArrowUpDown, ChevronLeft, ChevronRight,
  Instagram, Facebook, Linkedin, Youtube, Layers, Edit
} from 'lucide-react';
import LeadEditModal from './LeadEditModal';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/ui/dropdown-menu";
import { Button } from '@/ui/button';
import { Input } from '@/ui/input';
import { Badge } from '@/ui/badge';
import { Checkbox } from '@/ui/checkbox';
import API from '@/api/api01';
import { toast } from 'sonner';
import ImportLMSModal from '@/superadmin/ImportLMSModal';
import CSVImportLeadModal from '@/superadmin/CSVImportLeadModal';
import { exportToCSV } from '@/utils/exportUtils';

const STATUS_COLORS: any = {
  'New': 'bg-blue-100 text-blue-700 border-blue-200',
  'Verified': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Contacted': 'bg-amber-100 text-amber-700 border-amber-200',
  'Converted': 'bg-purple-100 text-purple-700 border-purple-200',
  'Lost': 'bg-slate-100 text-slate-700 border-slate-200',
};

const PLATFORM_ICONS: any = {
  Instagram: <Instagram size={14} className="text-[#E1306C]" />,
  Facebook: <Facebook size={14} className="text-[#1877F2]" />,
  LinkedIn: <Linkedin size={14} className="text-[#0A66C2]" />,
  YouTube: <Youtube size={14} className="text-[#FF0000]" />,
};

interface LeadManagerTableProps {
  onTabChange?: (tab: string) => void;
}

export default function LeadManagerTable({ onTabChange }: LeadManagerTableProps) {
  const [leads, setLeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [targetLeads, setTargetLeads] = useState<any[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<any | null>(null);


  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const res = await API.get('/superadmin/social-leads/unverified');
      setLeads(res.data);
      if (res.data.length > 0) {
        toast.success("Lead manager updated");
      } else {
        toast.error("No records found");
      }
    } catch (err) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (id: string) => {
    try {
      await API.put(`/superadmin/social-leads/${id}/verify`);
      toast.success("Lead verified successfully");
      fetchLeads();
    } catch (err) {
      toast.error("Verification failed");
    }
  };

  const handleDeleteLead = async (id: string) => {
    try {
      if (!confirm("Are you sure you want to delete this lead?")) return;
      await API.delete(`/superadmin/social-leads/${id}`);
      toast.success("Lead deleted");
      fetchLeads();
    } catch (err) {
      toast.error("Failed to delete lead");
    }
  };

  const handleBulkDelete = async () => {
    try {
      if (!confirm(`Delete ${selectedLeads.length} leads?`)) return;
      await API.post('/superadmin/social-leads/bulk-delete', { ids: selectedLeads });
      toast.success("Bulk delete successful");
      setSelectedLeads([]);
      fetchLeads();
    } catch (err) {
      toast.error("Bulk delete failed");
    }
  };

  const handleExportCSV = () => {
    if (filteredLeads.length === 0) {
      toast.error("No leads to export");
      return;
    }

    const exportData = filteredLeads.map(l => ({
      ID: l.id,
      Name: l.name,
      Email: l.email || 'N/A',
      Phone: l.phone || 'N/A',
      Platform: l.platform,
      Campaign: l.campaign_name || 'N/A',
      Score: l.lead_score,
      Value: l.lead_value || 0,
      Status: l.status,
      Source: l.lead_source || 'N/A',
      Notes: l.notes || ''
    }));

    exportToCSV(exportData, `social_leads_table_${new Date().toISOString().split('T')[0]}.csv`);
    toast.success("Filtered leads exported successfully");
  };

  const handleImportClick = (lead: any) => {
    setTargetLeads([lead]);
    setImportModalOpen(true);
  };

  const handleBulkImportClick = () => {
    const selectedObjects = leads.filter(l => selectedLeads.includes(l.id));
    setTargetLeads(selectedObjects);
    setImportModalOpen(true);
  };


  const toggleSelect = (id: string) => {
    setSelectedLeads(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const filteredLeads = leads.filter(l =>
    l.name.toLowerCase().includes(search.toLowerCase()) ||
    l.email?.toLowerCase().includes(search.toLowerCase()) ||
    l.platform.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-card rounded-2xl shadow-sm border border-white/10 overflow-hidden">
      {/* Table Header/Toolbar */}
      <div className="p-4 border-b border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search leads..."
              className="pl-9 h-10 rounded-xl border-white/5 bg-background focus:ring-1 focus:ring-primary/20 transition-all text-sm font-medium"
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            />

          </div>
          <Button variant="outline" size="icon" className="shrink-0 rounded-xl h-10 w-10 border-white/10 hover:bg-white/5">
            <Filter size={18} className="text-slate-500" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {selectedLeads.length > 0 && (
            <>
              <Button onClick={handleBulkDelete} variant="destructive" className="rounded-xl">
                Delete ({selectedLeads.length})
              </Button>
               <Button
                onClick={handleBulkImportClick}
                className="
                  bg-purple-600 hover:bg-purple-700
                  text-white
                  dark:bg-purple-500 dark:hover:bg-purple-600
                  rounded-xl
                  shadow-sm dark:shadow-[0_0_12px_rgba(168,85,247,0.25)]
                "
              >
                Batch Import to LMS ({selectedLeads.length})
              </Button>

            </>
          )}
          <Button
            variant="outline"
            onClick={handleExportCSV}
            className="
    rounded-xl
    border-slate-200 text-slate-600
    hover:bg-slate-100

    dark:border-purple-500/30
    dark:text-purple-300
    dark:hover:bg-purple-500/10
    dark:hover:text-purple-200
  "
          >
            <Download size={16} className="mr-2" />
            Export CSV
          </Button>

          <Button
            variant="outline"
            onClick={() => setCsvImportOpen(true)}
            className="
    rounded-xl
    border-blue-200 text-blue-600
    hover:bg-blue-50
    
    dark:border-blue-500/30
    dark:text-blue-300
    dark:hover:bg-blue-500/10
    dark:hover:text-blue-200
  "
          >
            <Upload size={16} className="mr-2" />
            Import CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="w-12 border-b border-white/5">
                <Checkbox
                  checked={selectedLeads.length === filteredLeads.length && filteredLeads.length > 0}
                  onCheckedChange={(checked: boolean) => {
                    if (checked) setSelectedLeads(filteredLeads.map(l => l.id));
                    else setSelectedLeads([]);
                  }}
                />
              </TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 border-b border-white/5">Lead ID</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 border-b border-white/5">Name</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 border-b border-white/5">Contact</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 border-b border-white/5">Platform</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 border-b border-white/5">Campaign</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 text-center border-b border-white/5">Score</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 border-b border-white/5">Value</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 border-b border-white/5">Status</TableHead>
              <TableHead className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 py-3 text-right border-b border-white/5 pr-4">Actions</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="h-40 text-center text-slate-400">Loading leads...</TableCell>
              </TableRow>
            ) : filteredLeads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center text-slate-400">
                    <Search className="h-12 w-12 mb-4 opacity-10" />
                    <p className="text-lg font-medium">No records found</p>
                    <p className="text-sm">Try adjusting your search or filters</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredLeads.map((lead) => (
              <TableRow key={lead.id} className="hover:bg-white/5 transition-colors border-white/5 cursor-pointer group">
                <TableCell>
                  <Checkbox
                    checked={selectedLeads.includes(lead.id)}
                    onCheckedChange={() => toggleSelect(lead.id)}
                  />
                </TableCell>
                <TableCell className="font-mono text-[10px] text-slate-400">
                  {lead.id.substring(0, 8)}...
                </TableCell>
                <TableCell>
                  <div className="text-[13px] font-bold text-slate-900 dark:text-white leading-tight">{lead.name}</div>
                  <div className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest font-bold">{lead.lead_source || 'Unknown Source'}</div>
                </TableCell>

                <TableCell>
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1.5 text-[12px] font-medium text-slate-600 dark:text-slate-400">
                      <Mail size={12} className="text-slate-400" />
                      {lead.email}
                    </div>
                    <div className="flex items-center gap-1.5 text-[12px] font-medium text-slate-600 dark:text-slate-400">
                      <Phone size={12} className="text-slate-400" />
                      {lead.phone}
                    </div>
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-slate-100 dark:bg-white/5">
                      {PLATFORM_ICONS[lead.platform] || <Layers size={14} />}
                    </div>
                    <span className="text-[13px] font-medium">{lead.platform}</span>
                  </div>
                </TableCell>

                <TableCell>
                  <div className="text-sm max-w-[150px] truncate" title={lead.campaign_name}>
                    {lead.campaign_name || 'No Campaign'}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className={`rounded-lg border-slate-200 ${lead.lead_score >= 80 ? 'text-emerald-500 bg-emerald-50' : lead.lead_score >= 50 ? 'text-amber-500 bg-amber-50' : 'text-slate-400'}`}>
                    {lead.lead_score || 0}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="font-bold text-slate-900 dark:text-white">${lead.lead_value || 0}</span>
                </TableCell>
                <TableCell>
                  <Badge className={`rounded-xl border shadow-none px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${STATUS_COLORS[lead.status] || 'bg-slate-100'}`}>
                    {lead.status}
                  </Badge>
                </TableCell>

                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0 rounded-lg hover:bg-slate-100">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 rounded-xl shadow-xl border-white/10 bg-card/95 backdrop-blur-xl">
                      <DropdownMenuLabel className="text-xs text-slate-400 font-normal">Actions</DropdownMenuLabel>
                      <DropdownMenuItem className="gap-2 focus:bg-purple-50 focus:text-purple-600 cursor-pointer">
                        <FileText size={14} /> View Details
                      </DropdownMenuItem>
                      {!lead.is_verified && (
                        <DropdownMenuItem
                          className="gap-2 focus:bg-emerald-50 focus:text-emerald-600 cursor-pointer"
                          onClick={() => handleVerify(lead.id)}
                        >
                          <UserCheck size={14} /> Verify Lead
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="gap-2 focus:bg-indigo-50 focus:text-indigo-600 cursor-pointer"
                        onClick={() => handleImportClick(lead)}
                      >
                        <ExternalLink size={14} /> Import to LMS
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="gap-2 focus:bg-blue-50 focus:text-blue-600 cursor-pointer"
                        onClick={() => { setEditingLead(lead); setIsEditModalOpen(true); }}
                      >
                        <Edit size={14} /> Edit Details
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="gap-2 focus:bg-red-50 focus:text-red-600 cursor-pointer text-red-500"
                        onClick={() => handleDeleteLead(lead.id)}
                      >
                        <Trash2 size={14} /> Delete Lead
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Container */}
      <div className="p-4 bg-slate-50/50 dark:bg-white/5 border-t border-white/10 flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
          Showing <strong className="text-slate-900 dark:text-white">{filteredLeads.length}</strong> leads
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 px-2 rounded-lg border-white/10 hover:bg-slate-100 dark:hover:bg-white/10" disabled>
            <ChevronLeft size={16} />
          </Button>
          <Button variant="outline" size="sm" className="h-8 px-2 rounded-lg border-white/10 hover:bg-slate-100 dark:hover:bg-white/10" disabled>
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>


      <ImportLMSModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        leads={targetLeads}
        onImportSuccess={() => {
          setSelectedLeads([]);
          fetchLeads();
        }}
      />


      <CSVImportLeadModal
        open={csvImportOpen}
        onOpenChange={setCsvImportOpen}
        onImportSuccess={fetchLeads}
      />

      <LeadEditModal 
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        lead={editingLead}
        type="social"
        onSuccess={fetchLeads}
      />
    </div>
  );
}
